import React from "react";
import {
    ShieldCheck,
    IdCard,
    HeartPulse,
    Home as HomeIcon,
    Building,
    Award,
    Car,
    Globe,
    Briefcase,
    GraduationCap,
    Star,
} from "lucide-react";

// Renforteo verified badges (spec §4). Each badge is only granted server-side when
// the corresponding document is in status "verified" (or the profile flag is true).
// Sensitive documents themselves are NEVER exposed to the client — only the boolean.
const BADGE_META = {
    identity: { label: "Identité vérifiée", icon: IdCard, color: "emerald" },
    health: { label: "Carte vitale vérifiée", icon: HeartPulse, color: "rose" },
    residence: { label: "Domicile vérifié", icon: HomeIcon, color: "sky" },
    siret: { label: "SIRET vérifié", icon: Building, color: "indigo" },
    // cnaps badge removed at launch — CNAPS activity not commercialized
    diploma: { label: "Diplôme vérifié", icon: GraduationCap, color: "violet" },
    attestation_vigilance: { label: "Attestation de vigilance vérifiée", icon: Award, color: "teal" },
    driving_license: { label: "Permis de conduire", icon: Car, color: "slate" },
    work_authorization: { label: "Droit au travail", icon: Briefcase, color: "green" },
    languages: { label: "Langues renseignées", icon: Globe, color: "blue" },
};

const COLOR_CLASSES = {
    emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
    rose: "bg-rose-50 text-rose-700 border-rose-200",
    sky: "bg-sky-50 text-sky-700 border-sky-200",
    indigo: "bg-indigo-50 text-indigo-700 border-indigo-200",
    amber: "bg-amber-50 text-amber-700 border-amber-200",
    violet: "bg-violet-50 text-violet-700 border-violet-200",
    teal: "bg-teal-50 text-teal-700 border-teal-200",
    slate: "bg-slate-50 text-slate-700 border-slate-200",
    green: "bg-green-50 text-green-700 border-green-200",
    blue: "bg-blue-50 text-blue-700 border-blue-200",
};

export function CandidateBadges({ badges = [], size = "sm" }) {
    if (!badges || badges.length === 0) return null;
    const cls = size === "xs" ? "text-[10px] px-1.5 py-0.5" : "text-xs px-2 py-1";
    return (
        <div className="flex flex-wrap gap-1" data-testid="candidate-badges">
            {badges.map((key) => {
                const meta = BADGE_META[key];
                if (!meta) return null;
                const Icon = meta.icon;
                return (
                    <span
                        key={key}
                        title={meta.label}
                        className={`inline-flex items-center gap-1 rounded-full border font-medium ${cls} ${COLOR_CLASSES[meta.color]}`}
                        data-testid={`badge-${key}`}
                    >
                        <Icon size={size === "xs" ? 10 : 12} />
                        <span className="hidden sm:inline">{meta.label.split(" ")[0]}</span>
                    </span>
                );
            })}
        </div>
    );
}

// Compatibility score pill — colour-coded, doesn't expose algorithm details (spec §3)
export function CompatScore({ score, testId }) {
    if (score == null) return null;
    const s = Math.max(0, Math.min(100, Number(score) || 0));
    const color =
        s >= 80 ? "bg-emerald-500 text-white" :
        s >= 60 ? "bg-[#F5A623] text-[#0B192C]" :
        s >= 40 ? "bg-slate-300 text-[#0B192C]" :
                  "bg-slate-200 text-slate-500";
    return (
        <span
            className={`inline-flex items-center gap-1 rounded-full px-2 py-1 text-xs font-bold ${color}`}
            title="Score de compatibilité (métier · distance · note · expérience · langues)"
            data-testid={testId || "compat-score"}
        >
            <span className="opacity-70 text-[10px] font-normal">Compat.</span>
            {s}
        </span>
    );
}

// Super Talent badge — attribué automatiquement quand rating ≥ 4.7 sur ≥ 10 missions
// et zéro no-show. Affiché avec priorité au-dessus des autres pills.
export function SuperTalentBadge({ size = "sm" }) {
    const cls = size === "xs" ? "text-[10px] px-2 py-0.5" : "text-xs px-2.5 py-1";
    return (
        <span
            className={`inline-flex items-center gap-1 rounded-full font-bold shadow-sm ${cls}`}
            style={{
                background: "linear-gradient(135deg, #F5A623 0%, #f7b74a 100%)",
                color: "#0B192C",
            }}
            title="Super Talent — Note ≥ 4,7/5 sur 10+ missions · 0 no-show"
            data-testid="badge-super-talent"
        >
            <Star size={size === "xs" ? 10 : 12} fill="#0B192C" />
            Super Talent
        </span>
    );
}
